import apiRoutes from "../apiRoutes";
import { BaseCrudService } from "../baseCrudService";
import { OrganizationData, OrganizationForm } from "../../models/organizationData";
import { AxiosResponse } from "axios";

type StartInstructionsStartResponse = { task_id: string };

type StartInstructionsStatusResponse = {
  task_id: string;
  state: string; // PENDING | STARTED | SUCCESS | FAILURE
  ready: boolean;
  failed: boolean;
};

/**
 * Parses a filename from a Content-Disposition header.
 * @param {string | undefined} header The Content-Disposition header value.
 * @returns {string | null} The decoded filename if present.
 */
function parseFilenameFromContentDisposition(header?: string): string | null {
  if (!header) return null;

  // attachment; filename="Startanleitungen.pdf"
  // or filename*=UTF-8''Startanleitungen.pdf
  const m = /filename\*?=(?:UTF-8''|")?([^";\n]+)"?/i.exec(header);
  if (!m?.[1]) return null;

  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

/**
 * Service class for managing organizations and start-instructions downloads.
 */
export class OrganizationService extends BaseCrudService {
  /**
   * Retrieves organizations for the current user.
   * @returns {Promise<OrganizationData[]>} A promise that resolves to organizations.
   */
  async getOrganizations() {
    const response = await this.getAll<OrganizationData[]>(apiRoutes.organization);
    return response.data;
  }

  /**
   * Retrieves an organization by ID.
   * @param {string} id The organization ID.
   * @returns {Promise<OrganizationData>} A promise that resolves to organization data.
   */
  async getOrganizationById(id: string) {
    const response = await this.get<OrganizationData>(apiRoutes.organization, id);
    return response.data;
  }

  /**
   * Creates a new organization.
   * @param {OrganizationForm} organization The organization payload.
   * @returns {Promise<OrganizationForm>} A promise that resolves to the created organization.
   */
  async addOrganization(organization: OrganizationForm) {
    const response = await this.create<OrganizationForm>(
      apiRoutes.organization,
      organization
    );
    return response.data;
  }

  /**
   * Updates an existing organization.
   * @param {string} id The organization ID.
   * @param {OrganizationForm} organizationData The updated organization payload.
   * @returns {Promise<AxiosResponse>} A promise that resolves to the update response.
   */
  async editOrganization(id: string, organizationData: OrganizationForm) {
    const response = await this.update<OrganizationForm>(
      apiRoutes.organization,
      id,
      organizationData
    );
    return response;
  }

  /**
   * Deletes an organization.
   * @param {string} id The organization ID.
   * @returns {Promise<AxiosResponse>} A promise that resolves to the delete response.
   */
  async deleteOrganization(id: string) {
    const response = await this.delete<OrganizationData>(apiRoutes.organization, id);
    return response;
  }

  /**
   * Starts generating start-instructions for the selected memberships.
   * @param {string} organizationId The organization ID.
   * @param {string[]} membershipIds Membership IDs to include.
   * @returns {Promise<StartInstructionsStartResponse>} A promise that resolves to the task id.
   */
  async startStartInstructions(organizationId: string, membershipIds: string[]) {
    const url =
      apiRoutes.organization +
      "/" +
      organizationId +
      apiRoutes.organization_generate_start_instructions;

    const response = await this.create<
      { membership_ids: string[] },
      AxiosResponse<StartInstructionsStartResponse>
    >(
      url,
      { membership_ids: membershipIds },
      undefined,
      { suppressSnackbars: true } // optional
    );

    return response.data; // { task_id }
  }

  /**
   * Retrieves the current status of a start-instructions task.
   * @param {string} organizationId The organization ID.
   * @param {string} taskId The task ID returned from startStartInstructions.
   * @returns {Promise<StartInstructionsStatusResponse>} A promise that resolves to task status.
   */
  async getStartInstructionsStatus(organizationId: string, taskId: string) {
    const url =
      apiRoutes.organization +
      "/" +
      organizationId +
      apiRoutes.organization_start_instructions_status_prefix +
      "/" +
      taskId +
      "/status";

    const response = await this.getAll<
      StartInstructionsStatusResponse,
      AxiosResponse<StartInstructionsStatusResponse>
    >(url, undefined, { suppressSnackbars: true });

    return response.data;
  }

  /**
   * Downloads the start-instructions PDF for a task.
   * @param {string} organizationId The organization ID.
   * @param {string} taskId The task ID returned from startStartInstructions.
   * @returns {Promise<{ blob: Blob; filename: string | null }>} A promise that resolves to the file blob and filename.
   */
  async downloadStartInstructionsByTaskId(organizationId: string, taskId: string) {
    const url =
      apiRoutes.organization +
      "/" +
      organizationId +
      apiRoutes.organization_download_start_instructions_prefix +
      "/" +
      taskId +
      "/download";

    const response = await this.getAll<Blob, AxiosResponse<Blob>>(url, undefined, {
      responseType: "blob",
      suppressSnackbars: true,
    });

    const cd = response.headers?.["content-disposition"];
    const filename = parseFilenameFromContentDisposition(cd);

    return { blob: response.data, filename };
  }
}
